import React, { useContext, useId } from 'react';
import { EmailHistoryRecipientRowOutDTO, EmailStageDTO, EmailStageStateEnum } from '@api/ringil-api.v2';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import SvgCreated from '@icons/Created';
import SvgAlertCircle from '@icons/AlertCircle';
import SvgMailSend from '@icons/mail-send.svg?react';
import SvgMail from '@icons/mail.svg?react';
import SvgMailOpened from '@icons/mail-opened.svg?react';
import { EMAIL_STAGES, EmailStageKey, stageLabel } from './emailHistoryLabels';

export type StageTone = 'done' | 'next' | 'later' | 'error';

/**
 * Reproduces all seven Figma `Message status` states: done is green, the first unfinished stage is
 * the dark "current" tone, later ones are muted. A failure stops progress, so nothing after it is
 * ever "current".
 */
export const resolveStageTone = (states: EmailStageStateEnum[], index: number): StageTone => {
  const state = states[index];
  if (state === EmailStageStateEnum.ERROR) return 'error';
  if (state === EmailStageStateEnum.DONE) return 'done';
  return index === states.findIndex(s => s !== EmailStageStateEnum.DONE) ? 'next' : 'later';
};

// Written out in full so Tailwind's scanner emits each class.
const TONE_CLASS: Record<StageTone, string> = {
  done: 'text-complementary-success',
  next: 'text-content-secondary',
  later: 'text-content-tertiary',
  error: 'text-complementary-error',
};

interface StageIcon {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  viewBox: string;
}

// The icon files hold the bare glyph without the padding of the Figma icon frame, so each viewBox
// is restated here to land the glyph exactly where the design puts it inside the 16px slot.
const STAGE_ICON: Record<EmailStageKey, StageIcon> = {
  queued: { Icon: SvgCreated, viewBox: '0.761719 0 12 12' },
  sent: { Icon: SvgMailSend, viewBox: '-2 -1 16 16' },
  delivered: { Icon: SvgMail, viewBox: '0 0 10 10' },
  opened: { Icon: SvgMailOpened, viewBox: '0 0 16 13.5' },
};

const ERROR_ICON: StageIcon = { Icon: SvgAlertCircle, viewBox: '0 0.5 16 16' };

// Figma leaves the read pill white; every other pill in every other state is #F7F7F7.
const pillBackground = (stage: EmailStageKey, tone: StageTone): string => {
  if (tone === 'error') return 'bg-complementary-error/10';
  if (stage === 'opened' && tone === 'done') return 'bg-white';
  return 'bg-base-tertiary';
};

interface ComponentProps {
  /** The four stages of one e-mail; any row DTO carrying them satisfies this. */
  stages: Pick<EmailHistoryRecipientRowOutDTO, EmailStageKey>;
}

const EmailStageStrip: React.FC<ComponentProps> = ({ stages: row }) => {
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { tr } = useTranslationLgs();
  const baseId = `email-stage-${useId().replace(/:/g, '')}`;

  const stages: (EmailStageDTO | undefined)[] = [row.queued, row.sent, row.delivered, row.opened];
  const states = stages.map(s => s?.emailStageState ?? EmailStageStateEnum.WAITING);

  return (
    <div className="inline-flex items-center gap-1.5">
      {EMAIL_STAGES.map((stage, index) => {
        const tone = resolveStageTone(states, index);
        const { Icon, viewBox } = tone === 'error' ? ERROR_ICON : STAGE_ICON[stage];
        const targetId = `${baseId}-${stage}`;
        const at = stages[index]?.at;
        const detail = stages[index]?.detail;
        return (
          <React.Fragment key={stage}>
            <span
              id={targetId}
              className={`inline-flex items-center p-[5px] rounded-full cursor-help ${pillBackground(stage, tone)} ${TONE_CLASS[tone]}`}>
              <Icon className="w-4 h-4" viewBox={viewBox} />
            </span>
            <CustomTooltip
              targetSelector={`#${targetId}`}
              position="top"
              content={
                <div className="flex flex-col gap-1">
                  <div>
                    {stageLabel(tr, stage, states[index])}
                    {at ? `: ${stringDateTimeWithLocaleFromApiStringInTz(at, loggedUser?.defaultTimezoneId)}` : ''}
                  </div>
                  {detail && <div>{`${tr('EmailHistory.status', 'Status')}: ${detail}`}</div>}
                </div>
              }
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default EmailStageStrip;
